import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { clientActions } from '../../store/client.slice';

import type { AppDispatch } from '../../store';
import type { IClientModifierMatrixRow } from '../../utils/types';

// Wraps the client modifier-matrix mutation thunks (create/update/delete) plus a re-fetch so the
// section component stays free of direct `useDispatch` use. Each mutation returns the dispatched
// thunk promise so the caller can `.unwrap()` and re-fetch on success.
export const useClientModifierMatrixActions = (clientId: string | undefined) => {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();

  const refetchRows = () =>
    dispatch(clientActions.fetchClientModifierMatrix({ clientId, getAccessTokenSilently }));

  const createRow = (data: Omit<IClientModifierMatrixRow, 'id'>) =>
    dispatch(
      clientActions.createClientModifierMatrixRow({ data, clientId, getAccessTokenSilently }),
    );

  // Like create, the PUT body carries the id-less row fields (full-row replace); the row id is
  // the URL param.
  const updateRow = (data: Omit<IClientModifierMatrixRow, 'id'>, rowId: number) =>
    dispatch(
      clientActions.updateClientModifierMatrixRow({ data, rowId, clientId, getAccessTokenSilently }),
    );

  const deleteRow = (rowId: number | null) =>
    dispatch(
      clientActions.deleteClientModifierMatrixRow({ rowId, clientId, getAccessTokenSilently }),
    );

  return { refetchRows, createRow, updateRow, deleteRow };
};
